import { ConnectedUsersStatusEnum } from '../waha';

export enum Hierarchy {
  WORKSPACE = 'Workspace',
  DIVISION = 'Division',
  CUSTOMER_GROUPS = 'CustomerGroup',
  CUSTOMER = 'Customer',
}

export enum ConfigModule {
  CATALOGUE = 'Catalogue',
  PROMOTIONS = 'Promotions',
  ORDER = 'Order',
  INVOICE = 'Invoice',
  CUSTOMER = 'Customer',
  TAX = 'Tax',
  MFA = 'Mfa',
  CREDIT_LIMIT = 'Credit Limit',
  WBA = 'WBA',
  PRICING = 'Pricing',
  FIELD_AUTOMATION = 'Field Automation',
  TURNOVER = 'Turnover',
  SELLER_HUB = 'Seller Hub',
}

export interface ConfigSummaryRequest {
  workspaceId: string;
  hierarchy?: Hierarchy;
  entityIds?: string[];
}

export interface ConfigSummaryResponse {
  workspaceId: string;
  noOfSettings: string;
  noOfModule: string;
  lastUpdatedAt: string;
  divisionId?: string;
  customerGroupId?: string;
  customerId?: string;
  teamMemberId?: string;
}
export type ConfigSummary = ConfigSummaryResponse;
export interface ConfigDetailsRequest {
  workspaceId: string;
  divisionId?: string;
  customerGroupId?: string;
  customerId?: string;
  teamMemberId?: string;
  module: ConfigModule;
  rollback: boolean;
}

export enum CreditLimitStrategy {
  DIVISON_CREDIT_LIMIT = 'Division credit limit',
  OVER_ALL_CREDIT_LIMIT = 'Over all credit limit',
  NONE = 'None',
}

export enum OrderSplitBy {
  cfaDivision = 'cfa-division',
  cfa = 'cfa',
  cfaHeaderDivision = 'cfa-header-division',
}

export interface Config {
  pricingStrategy?: string;
  displayPricesInCatalogue?: boolean;
  displayOutOfStock?: boolean;
  setMOQLimit?: string;
  productMOQRule?: string;
  cfaApprovalRequired?: boolean;
  orderCutOffTime?: number;
  MOQ_delta?: number;
  priceCalculationStrategy?: string;
  distributorMargin?: number;
  MFA_workspace?: boolean;
  displayCreditLimitOnCheckout?: boolean;
  displayCreditLimitStrategy?: CreditLimitStrategy;
  displayCreditLimitOnApproval?: boolean;
  orderSplitBy?: OrderSplitBy;
  catalogueView?: CatalogueView;
  enableGeoTagging?: boolean;
  geoFencingRadius?: number;
  enableTripStockEntry?: boolean;
  disableRetailingWithoutCheckIn?: boolean;
  schemeType?: 'POINTS' | 'VALUE';
  schema_type?: string;
  selection_type?: string;
  slab_application_level?: string;
  priceRoundOff?: boolean;
  priceRoundOffStrategy?: string;
  cashDiscount?: string;
  advancePayment?: {
    paymentTerms: string;
    customerGroups: {
      id: string;
      name: string;
    }[];
    advancePercentage: number;
  };
}

export interface ConfigDetailsResponse {
  id: string;
  createdDate: string;
  updatedDate: string;
  config: Config;
  workspaceId: string;
  divisionId: string | null;
  customerGroupId: string | null;
  customerId: string | null;
  teamMemberId: string | null;
  module: ConfigModule;
}

export type CreateConfigSettingsResponse = ConfigDetailsResponse;
export interface CreateConfigSettingsRequest {
  config: Config;
  module: ConfigModule;
  hierarchy: Hierarchy;
  workspaceId: string;
  divisionId?: string;
  customerGroupId?: string;
  customerId?: string;
  teamMemberId?: string;
}

export interface ConfigSchemaRequest {
  module?: ConfigModule;
  workspaceId: string;
}

export interface ConfigSetting {
  description: string;
  type: string;
  inputType: string;
  options: number[] | string[];
  hierarchy: string[];
  required: boolean;
  settingName?: string;
  default?: string;
}

export interface ConfigSchema {
  cfaApprovalRequired?: ConfigSetting;
  orderCutOffTime?: ConfigSetting;
  MOQ_delta?: ConfigSetting;
  priceCalculationStrategy?: ConfigSetting;
  distributorMargin?: ConfigSetting;
  priceRoundOff?: ConfigSetting;
  priceRoundOffStrategy?: ConfigSetting;
  module: string;
}

export interface WorkspaceConfigList {
  id: string;
  moduleName: string;
  activeSettings: string;
  lastUpdated: string;
}

export enum CatalogueView {
  ListWithImagesView = 'ListWithImagesView',
  ThumbnailView = 'ThumbnailView',
  ListView = 'ListView',
}

export interface FieldAutomationConfig {
  enableGeoTagging: boolean;
  geoFencingRadius: number;
  enableTripStockEntry: boolean;
  disableRetailingWithoutCheckIn: boolean;
}

export interface WabaTemplateResponse {
  id: string;
  createdDate: string;
  updatedDate: string;
  workspaceId: string;
  wabaId: string;
  whatsappPhoneNoId: any;
  whatsappPhoneNo: string;
  namespaceId: any;
  userToken: string;
  provider: string;
  sid: any;
  qualityRating: any;
  messageLimit: any;
  isDefault: boolean;
  active: boolean;
  fbmId: string;
  status: string;
  displayName: string;
  profilePic: any;
  projectId: string;
  branch: string;
  tag: string;
  esLinkID: any;
  info: any;
  isCatalogueSyncEnable: boolean;
  wabaTemplates: EachWabaTemplatesRes[];
  pagination: TemplateDetailsPagination;
  refreshedMessageLimit: any;
  refreshedQualityRating: any;
  /** Meta-side Marketing Messages Lite */
  metaMarketingMessagesLiteEnabled?: boolean;
  /** API enablement for MM Lite on Zono */
  marketingMessagesLiteApiEnabled?: boolean;
  facebookPageId: string;
  catalogueName: string;
  catalogueID: string;
  productsSynced: number;
  lastSyncedDate: string;
  catalogueStatus: string;
}

export interface EachWabaTemplatesRes {
  MetaTag: any;
  id: string;
  createdDate: string;
  updatedDate: string;
  wabaId: string;
  category: string;
  whatsappConfigId: string;
  templateId: string;
  templateBody: TemplateBodyRes;
  templateName: string;
  status: string;
  health: string;
  branch: string;
  tag: string;
  language: string;
  action: string;
  isCustomized: boolean;
  templateDetails: TemplateDetailsRes;
  uploadStatusMessage: string;
}

export interface TemplateDetailsRes {
  name: string;
  parameter_format: string;
  components: TemplateDetailsResComponent[];
  language: string;
  status: string;
  category: string;
  id: string;
}
export interface TemplateBodyRes {
  name: string;
  category: string;
  components: TemplateDetailsResComponent[];
  language: string;
}

export interface TemplateDetailsResComponent {
  type: string;
  text?: string;
  example?: TemplateDetailsExample;
  cards?: TemplateDetailsCard[];
}

export interface TemplateDetailsExample {
  body_text: string[][];
}

export interface TemplateDetailsCard {
  components: Component2[];
}

export interface Component2 {
  type: string;
  format?: string;
  example?: Example2;
  text?: string;
  buttons?: ComponentButton[];
}

export interface Example2 {
  header_handle?: string[];
  body_text?: string[][];
}

export interface ComponentButton {
  type: string;
  text: string;
  url?: string;
  example?: string[];
}

export interface TemplateDetailsPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type TemplateStatus =
  | 'REJECTED'
  | 'APPROVED'
  | 'PENDING_DELETION'
  | 'FAIL'
  | 'SUCCESS';

export type Languages = 'en' | 'hi';

export type TemplateCategory = 'UTILITY' | 'MARKETING';

export enum ComponentTypeEnum {
  CAROUSEL = 'CAROUSEL',
  BODY = 'BODY',
  HEADER = 'HEADER',
  FOOTER = 'FOOTER',
  BUTTONS = 'BUTTONS',
}

export enum ButtonTypeEnum {
  URL = 'URL',
  FLOW = 'FLOW',
  QUICK_REPLY = 'QUICK_REPLY',
}

export enum ButtonURLSubTypeEnum {
  VIEW_CATALOGUE = 'catalogue_url',
  VIEW_PRODUCT = 'product_url',
  VIEW_ORDER = 'order_url',
  VIEW_CAMPAIGN = 'campaign_url',
  REORDER = 'reorder_url',
  CUSTOM_URL = 'custom_url',
  SURVEY_FORM = 'form_url',
}

// Display texts for button types
export const ButtonTypeDisplayText: Record<ButtonTypeEnum, string> = {
  [ButtonTypeEnum.URL]: 'URL',
  [ButtonTypeEnum.FLOW]: 'Flow',
  [ButtonTypeEnum.QUICK_REPLY]: 'Quick Reply',
};

// Button type constraints
export const ButtonTypeConstraints: Record<
  ButtonTypeEnum,
  { maxCount: number; description: string }
> = {
  [ButtonTypeEnum.URL]: { maxCount: 2, description: '2 buttons maximum' },
  [ButtonTypeEnum.FLOW]: { maxCount: 1, description: '1 button maximum' },
  [ButtonTypeEnum.QUICK_REPLY]: {
    maxCount: 2,
    description: '2 replies maximum',
  },
};

export const ButtonURLSubTypeDisplayText: Record<ButtonURLSubTypeEnum, string> =
  {
    [ButtonURLSubTypeEnum.VIEW_CATALOGUE]: 'View Catalogue',
    [ButtonURLSubTypeEnum.VIEW_PRODUCT]: 'View Product',
    [ButtonURLSubTypeEnum.VIEW_ORDER]: 'View Order',
    [ButtonURLSubTypeEnum.VIEW_CAMPAIGN]: 'View Campaign',
    [ButtonURLSubTypeEnum.REORDER]: 'Reorder',
    [ButtonURLSubTypeEnum.CUSTOM_URL]: 'Custom URL',
    [ButtonURLSubTypeEnum.SURVEY_FORM]: 'Form URL',
  };

export const ButtonURLSubTypeURLTemplate: Record<ButtonURLSubTypeEnum, string> =
  {
    [ButtonURLSubTypeEnum.VIEW_CATALOGUE]:
      'https://mobile.zotok.ai/{{catalogue_url}}',
    [ButtonURLSubTypeEnum.VIEW_PRODUCT]:
      'https://mobile.zotok.ai/{{product_url}}',
    [ButtonURLSubTypeEnum.VIEW_ORDER]: 'https://mobile.zotok.ai/{{order_url}}',
    [ButtonURLSubTypeEnum.VIEW_CAMPAIGN]:
      'https://mobile.zotok.ai/{{campaign_url}}',
    [ButtonURLSubTypeEnum.REORDER]: 'https://mobile.zotok.ai/{{reorder_url}}',
    [ButtonURLSubTypeEnum.CUSTOM_URL]: '',
    [ButtonURLSubTypeEnum.SURVEY_FORM]: 'https://mobile.zotok.ai/{{1}}',
  };

export interface ButtonType {
  type: ButtonTypeEnum;
  text: string;
  url?: string;
  example?: string[];
  _metadata?: {
    urlSubType?: ButtonURLSubTypeEnum;
    isExpanded?: boolean;
  };
}

export interface UIButtonType extends ButtonType {
  id: string; // Temporary ID for drag and drop
  _metadata?: {
    urlSubType?: ButtonURLSubTypeEnum;
    isExpanded?: boolean;
  };
}

export interface BodyComponent {
  type: ComponentTypeEnum.BODY;
  text: string;
  example: { body_text: string[][] };
}

export interface HeaderComponent {
  type: ComponentTypeEnum.HEADER;
  format: 'TEXT' | 'IMAGE' | 'VIDEO' | 'LOCATION' | 'DOCUMENT' | 'None';
  text?: string; // Only if format is TEXT
  example?: { header_handle: string[] };
}

export interface FooterComponent {
  type: ComponentTypeEnum.FOOTER;
  text: string;
}

export interface ButtonsComponent {
  type: ComponentTypeEnum.BUTTONS;
  buttons: ButtonType[];
}

// Discriminated union for ComponentType
export type ComponentType =
  | BodyComponent
  | HeaderComponent
  | FooterComponent
  | CarouselComponent
  | ButtonsComponent;

interface CarouselCardType {
  components: ComponentType[];
}

export interface CarouselComponent {
  type: ComponentTypeEnum.CAROUSEL;
  cards: CarouselCardType[];
}

export interface TemplateFilterPayload {
  status: TemplateStatus[];
  language: Languages[];
  category: TemplateCategory[];
  MetaTag?: string;
}
export interface TemplateFilterRequest {
  payload: TemplateFilterPayload;
  sellerWorkspaceId: string;
}

export interface VariableOptions {
  name: string;
  value: string;
}

export interface consumptionReportRequest {
  startDate?: string;
  endDate?: string;
  sellerWorkspaceId: string;
}

export interface ConsumptionMetricResponse {
  data: ConSumptionMetricData[];
  total_auth_conversation_count: number;
  total_marketing_conversation_count: number;
  total_utility_conversation_count: number;
  total_service_conversation_count: number;
  total_auth_conversation_charge: string;
  total_marketing_conversation_charge: string;
  total_utility_conversation_charge: string;
  total_service_conversation_charge: string;
  total_count: number;
  total_charge: string;
}

export interface ConSumptionMetricData {
  workspaceId: string;
  whatsAppId: string;
  projectId: string;
  date: string;
  auth_conversation_count: number;
  auth_conversation_charge: string;
  marketing_conversation_count: number;
  marketing_conversation_charge: string;
  utility_conversation_count: number;
  utility_conversation_charge: string;
  service_conversation_count: number;
  service_conversation_charge: string;
}

export interface RefreshTemplateRequest {
  sellerWorkspaceId: string;
  payload: {
    waConfigId: string;
  };
}

export interface WABACatalogueDetails {
  catalogId: string;
  catalogName: string;
  wabaId: string;
  lastSyncedAt: string;
  productsCount: number;
  message: string;
  facebookPageId: string;
  status: string;
  catalogueId?: string;
}
export interface RefreshTemplateResponse {
  message: string;
  catalogDetails: WABACatalogueDetails;
  data: {
    id: string;
    createdDate: string;
    updatedDate: string;
    workspaceId: string;
    wabaId: string;
    fbmId: string;
    whatsappPhoneNoId: string;
    whatsappPhoneNo: string;
    namespaceId: string;
    metaBusinessToken: string;
    provider: string;
    sid: string;
    qualityRating: string;
    messageLimit: number;
    isDefault: boolean;
    isShared: boolean;
    status: string;
    displayName: string;
    profilePic: string;
    isCoexistence: boolean;
    projectId: string;
    branch: string;
    tag: string;
    esLinkID: string;
    info: string;
  };
}

export interface TemplateListResponse {
  id: string;
  createdDate: string;
  updatedDate: string;
  workspaceId: string;
  wabaId: string;
  whatsappPhoneNoId: string | null;
  whatsappPhoneNo: string;
  namespaceId: string | null;
  userToken: string;
  metaBusinessToken: string;
  provider: string;
  sid: null;
  qualityRating: string | null;
  messageLimit: number | null;
  isDefault: boolean;
  isShared: boolean;
  active: boolean;
  isDeleted: boolean;
  deletedAt: null;
  status: ConnectedUsersStatusEnum;
  displayName: string;
  authCode: null;
  profilePic: null;
  projectId: string;
  isCoexistence: boolean;
  branch: string;
  tag: string;
  esLinkID: null;
  info: null;
  isCatalogueSyncEnable: boolean;
  fbmId: string;
  wabaTemplates: EachWabaTemplatesRes[];
  refreshedQualityRating: any;
  refreshedMessageLimit: any;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    startRecord: number;
    endRecord: number;
  };
}
export type ShippingMode = 'weight-value-tier' | 'order-value-tier';

export interface ShippingChargesListItem {
  type: ShippingMode;
  name: string;
  id: string;
}

export interface GetShippingChargesListResponse {
  items: ShippingChargesListItem[];
  totalItems: number;
}
export interface GetShippingChargesListRequest {
  sellerWorkspaceId: string;
}

export interface GetShippingChargesByIdResponse {
  calculator: {
    code: ShippingMode;
    args: {
      name: string;
      value: string;
    }[];
  };
  name: string;
  pincodes: {
    id: number;
    channelId: number;
  }[];
}
export interface GetShippingChargesByIdRequest {
  sellerWorkspaceId: string;
  shippingMethodId: string;
}

export interface DeleteShippingChargesRequest {
  sellerWorkspaceId: string;
  shippingMethodId: string;
}

export interface CreateShippingChargesRequest {
  shippingId?: string;
  params: {
    sellerWorkspaceId: string;
  };
  body: any;
}

export interface UpdateSequenceOfShippingChargesRequest {
  params: {
    sellerWorkspaceId: string;
  };
  body: {
    shippingMethods: {
      id: number;
      sequence: number;
    }[];
  };
}

export interface UpdateAdvancePaymentResponse {
  id: string;
  createdDate: string;
  updatedDate: string;
  config: Config;
  module: ConfigModule;
  workspaceId: string;
}

export interface UpdateAdvancePaymentRequest {
  module: ConfigModule;
  hierarchy: Hierarchy;
  workspaceId: string;
  config: {
    advancePayment: {
      customerGroups: {
        id: string;
        name: string;
      }[];
      advancePercentage: number;
      paymentTerms: string;
    };
  };
}
